import {
  type ChangePasswordInput,
  changePasswordInput,
  type MeResponse,
  type UpdateProfileInput,
  updateProfileInput,
} from '@facture/contracts'
import { zodResolver } from '@hookform/resolvers/zod'
import { useMutation } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import { useForm } from 'react-hook-form'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card'
import { Field } from '@/components/ui/field'
import { Input } from '@/components/ui/input'
import { Avatar } from '@/components/ui/misc'
import { SessionsCard } from '@/features/auth/sessions-card'
import { ApiRequestError, api } from '@/lib/api'
import { useSession } from '@/lib/session'

export const Route = createFileRoute('/_app/settings/profile')({
  staticData: { crumb: 'Mi perfil' },
  component: ProfileSettings,
})

function ProfileSettings() {
  const user = useSession((s) => s.user)
  const profile = useForm<UpdateProfileInput>({
    resolver: zodResolver(updateProfileInput),
    values: { name: user?.name ?? '' },
  })
  const password = useForm<ChangePasswordInput>({
    resolver: zodResolver(changePasswordInput),
    defaultValues: { currentPassword: '', newPassword: '' },
  })

  const saveProfile = useMutation({
    mutationFn: (values: UpdateProfileInput) => api.patch<MeResponse>('/auth/me', values, { global: true }),
    onSuccess: (me) => {
      useSession.getState().setSession(me)
      toast.success('Perfil actualizado')
    },
  })

  const changePassword = useMutation({
    mutationFn: (values: ChangePasswordInput) => api.post('/auth/password', values, { global: true }),
    onSuccess: () => {
      password.reset()
      toast.success('Contraseña actualizada. Cerramos tus otras sesiones.')
    },
    onError: (error) => {
      if (error instanceof ApiRequestError && error.code === 'invalid_credentials') {
        password.setError('currentPassword', { message: error.message })
      } else toast.error(error.message)
    },
  })

  if (!user) return null

  return (
    <div className="grid gap-6">
      <Card>
        <form onSubmit={profile.handleSubmit((v) => saveProfile.mutate(v))} noValidate>
          <CardHeader>
            <div>
              <CardTitle>Perfil</CardTitle>
              <CardDescription>
                Tu nombre aparece en la bitácora y en los comprobantes que emitís.
              </CardDescription>
            </div>
          </CardHeader>
          <CardContent className="grid gap-5">
            <div className="flex items-center gap-4">
              <Avatar name={user.name} src={user.avatarUrl} className="size-14 text-base" />
              <div>
                <div className="font-medium">{user.name}</div>
                <div className="text-muted-foreground text-sm">{user.email}</div>
              </div>
            </div>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <Field label="Nombre" error={profile.formState.errors.name} required>
                {(c) => <Input {...c} {...profile.register('name')} />}
              </Field>
              <Field label="Correo electrónico" hint="Pedile a un administrador que lo cambie.">
                {(c) => <Input {...c} value={user.email} disabled />}
              </Field>
            </div>
          </CardContent>
          <CardFooter>
            <Button type="submit" loading={saveProfile.isPending} disabled={!profile.formState.isDirty}>
              Guardar
            </Button>
          </CardFooter>
        </form>
      </Card>

      <Card>
        <form onSubmit={password.handleSubmit((v) => changePassword.mutate(v))} noValidate>
          <CardHeader>
            <div>
              <CardTitle>Contraseña</CardTitle>
              <CardDescription>Mínimo 10 caracteres con letras y números.</CardDescription>
            </div>
          </CardHeader>
          <CardContent className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <Field label="Contraseña actual" error={password.formState.errors.currentPassword} required>
              {(c) => (
                <Input
                  {...c}
                  {...password.register('currentPassword')}
                  type="password"
                  autoComplete="current-password"
                />
              )}
            </Field>
            <Field label="Nueva contraseña" error={password.formState.errors.newPassword} required>
              {(c) => (
                <Input
                  {...c}
                  {...password.register('newPassword')}
                  type="password"
                  autoComplete="new-password"
                />
              )}
            </Field>
          </CardContent>
          <CardFooter>
            <Button type="submit" loading={changePassword.isPending}>
              Cambiar contraseña
            </Button>
          </CardFooter>
        </form>
      </Card>

      <SessionsCard />
    </div>
  )
}
